import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';

interface Departamento {
  id: string;
  nome: string;
}

type TipoDocumento = 'IN' | 'POP' | 'Formulário' | 'Manual' | 'Guia Prático' | 'Política' | 'Comunicado' | 'Outro';

const TIPOS_DOCUMENTO: { value: TipoDocumento; label: string }[] = [
  { value: 'IN', label: 'Instrução Normativa (IN)' },
  { value: 'POP', label: 'POP — Procedimento Operacional Padrão' },
  { value: 'Política', label: 'Política' },
  { value: 'Manual', label: 'Manual' },
  { value: 'Guia Prático', label: 'Guia Prático' },
  { value: 'Formulário', label: 'Formulário' },
  { value: 'Comunicado', label: 'Comunicado' },
  { value: 'Outro', label: 'Outro' },
];

// Classes Tailwind (não style inline) — só assim o CSS de modo escuro
// consegue interceptar e escurecer esses fundos. Cores em style={{}} direto
// no elemento nunca são alcançadas pelos seletores `.dark .bg-x` do CSS
// global, então esses badges ficavam sempre "modo claro" independente do
// tema escolhido pelo usuário.
const TIPO_CORES: Record<TipoDocumento, string> = {
  'IN':           'bg-blue-100 text-blue-800',
  'POP':          'bg-green-100 text-green-800',
  'Política':     'bg-yellow-100 text-yellow-800',
  'Manual':       'bg-purple-100 text-purple-800',
  'Guia Prático': 'bg-orange-100 text-orange-800',
  'Formulário':   'bg-indigo-100 text-indigo-800',
  'Comunicado':   'bg-amber-100 text-amber-800',
  'Outro':        'bg-gray-100 text-gray-700',
};

interface ArquivoInfo {
  id?: number;
  nome_arquivo?: string;
  nome_original: string;
  tipo: string;
  tamanho: number;
}

interface Documento {
  id: number;
  departamento_id: string;
  departamento_nome: string;
  tipo: TipoDocumento;
  titulo: string;
  conteudo: string;
  ativo: boolean;
  criado_por_nome: string;
  criado_em: string;
  atualizado_em: string;
  arquivos: ArquivoInfo[] | null;
}

interface FormData {
  departamento_id: string;
  tipo: TipoDocumento;
  titulo: string;
  conteudo: string;
  ativo: boolean;
}

const FORM_VAZIO: FormData = { departamento_id: '', tipo: 'IN', titulo: '', conteudo: '', ativo: true };

const tipoBadge = (tipo: string) => {
  const classes = TIPO_CORES[tipo as TipoDocumento] ?? TIPO_CORES['Outro'];
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-bold tracking-wide whitespace-nowrap ${classes}`}>
      {tipo}
    </span>
  );
};

const formatarTamanho = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const iconeArquivo = (tipo: string) => {
  if (tipo?.includes('pdf')) {
    return (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    );
  }
  if (tipo?.includes('word') || tipo?.includes('docx')) {
    return (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    );
  }
  if (tipo?.includes('sheet') || tipo?.includes('excel') || tipo?.includes('xlsx')) {
    return (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m6 10V7M5 5h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z" />
      </svg>
    );
  }
  if (tipo?.includes('csv')) {
    return (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m6 10V7M5 5h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z" />
      </svg>
    );
  }
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
    </svg>
  );
};

const DocumentosIAPage: React.FC = () => {
  const [documentos, setDocumentos] = useState<Documento[]>([]);
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [filtroDept, setFiltroDept] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando] = useState<Documento | null>(null);
  const [form, setForm] = useState<FormData>(FORM_VAZIO);
  const [arquivoImportado, setArquivoImportado] = useState<ArquivoInfo | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [extraindo, setExtraindo] = useState(false);
  const [erro, setErro] = useState('');
  const [confirmandoId, setConfirmandoId] = useState<number | null>(null);
  const [expandidos, setExpandidos] = useState<Set<number>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { carregarDados(); }, []);

  const carregarDados = async () => {
    setCarregando(true);
    try {
      const [docsRes, deptsRes] = await Promise.all([
        api.get('/documentos-ia'),
        api.get('/departamentos'),
      ]);
      setDocumentos(docsRes.data.documentos || []);
      setDepartamentos(deptsRes.data.departamentos || deptsRes.data || []);
    } catch {
      setErro('Erro ao carregar dados.');
    } finally {
      setCarregando(false);
    }
  };

  const abrirCriar = () => {
    setEditando(null);
    setForm(FORM_VAZIO);
    setArquivoImportado(null);
    setErro('');
    setModalAberto(true);
  };

  const abrirEditar = async (doc: Documento) => {
    try {
      const res = await api.get(`/documentos-ia/${doc.id}`);
      const d = res.data.documento;
      setEditando(doc);
      setForm({ departamento_id: d.departamento_id, tipo: d.tipo, titulo: d.titulo, conteudo: d.conteudo, ativo: d.ativo });
      setArquivoImportado(null);
      setErro('');
      setModalAberto(true);
    } catch {
      setErro('Erro ao carregar documento.');
    }
  };

  const handleImportarArquivo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const arquivo = e.target.files?.[0];
    if (!arquivo) return;
    setExtraindo(true);
    setErro('');
    try {
      const formData = new FormData();
      formData.append('arquivo', arquivo);
      const res = await api.post('/documentos-ia/extrair-arquivo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm(f => ({
        ...f,
        conteudo: res.data.conteudo,
        titulo: f.titulo || arquivo.name.replace(/\.[^.]+$/, ''),
      }));
      setArquivoImportado(res.data.arquivo);
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Erro ao processar o arquivo.');
    } finally {
      setExtraindo(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const salvar = async () => {
    if (!form.departamento_id || !form.titulo.trim() || !form.conteudo.trim()) {
      setErro('Preencha todos os campos obrigatórios.');
      return;
    }
    setSalvando(true);
    setErro('');
    try {
      const payload = { ...form, arquivo: arquivoImportado || undefined };
      if (editando) {
        await api.put(`/documentos-ia/${editando.id}`, payload);
      } else {
        await api.post('/documentos-ia', payload);
      }
      setModalAberto(false);
      await carregarDados();
    } catch (e: any) {
      setErro(e?.response?.data?.message || 'Erro ao salvar documento.');
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async (id: number) => {
    try {
      await api.delete(`/documentos-ia/${id}`);
      setConfirmandoId(null);
      await carregarDados();
    } catch {
      setErro('Erro ao excluir documento.');
    }
  };

  const baixarArquivo = async (docId: number, fileId: number) => {
    try {
      const res = await api.get(`/documentos-ia/${docId}/arquivos/${fileId}/download`);
      window.open(res.data.url, '_blank');
    } catch {
      toast.error('Erro ao gerar link de download.');
    }
  };

  const excluirArquivo = async (docId: number, fileId: number) => {
    try {
      await api.delete(`/documentos-ia/${docId}/arquivos/${fileId}`);
      await carregarDados();
    } catch {
      toast.error('Erro ao remover arquivo.');
    }
  };

  const toggleExpandido = (id: number) => {
    setExpandidos(prev => {
      const novo = new Set(prev);
      novo.has(id) ? novo.delete(id) : novo.add(id);
      return novo;
    });
  };

  const docsFiltrados = documentos.filter(d => {
    if (filtroDept && d.departamento_id !== filtroDept) return false;
    if (filtroTipo && d.tipo !== filtroTipo) return false;
    return true;
  });

  const porDepto: Record<string, Documento[]> = {};
  for (const d of docsFiltrados) {
    if (!porDepto[d.departamento_nome]) porDepto[d.departamento_nome] = [];
    porDepto[d.departamento_nome].push(d);
  }

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Documentos da Bravo AI</h1>
          <p className="text-sm text-gray-500 mt-1">
            Gerencie Instruções Normativas (IN) e POPs por departamento. Cada departamento acessa apenas os seus.
          </p>
        </div>
        <button
          onClick={abrirCriar}
          className="flex items-center gap-2 bg-bravo-primary hover:bg-red-700 text-white font-semibold px-4 py-2 rounded-lg transition-colors"
        >
          <span className="text-lg leading-none">+</span> Novo Documento
        </button>
      </div>

      {/* Filtros */}
      <div className="flex gap-3 mb-5">
        <select
          value={filtroDept}
          onChange={e => setFiltroDept(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
        >
          <option value="">Todos os departamentos</option>
          {departamentos.map(d => <option key={d.id} value={d.id}>{d.nome}</option>)}
        </select>
        <select
          value={filtroTipo}
          onChange={e => setFiltroTipo(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
        >
          <option value="">Todos os tipos</option>
          {TIPOS_DOCUMENTO.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <span className="self-center text-sm text-gray-400">{docsFiltrados.length} documento(s)</span>
      </div>

      {/* Conteúdo */}
      {carregando ? (
        <div className="text-center py-16 text-gray-400">Carregando documentos...</div>
      ) : docsFiltrados.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          <div className="font-medium text-gray-500">Nenhum documento cadastrado ainda.</div>
          <div className="text-sm mt-1">Clique em "Novo Documento" para começar.</div>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {Object.entries(porDepto).map(([dept, docs]) => (
            <div key={dept} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
              <div className="px-5 py-3 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <span className="font-semibold text-gray-700 dark:text-gray-200 text-sm">{dept}</span>
                <span className="text-xs text-gray-400">{docs.length} documento(s)</span>
              </div>
              <div className="divide-y divide-gray-100">
                {docs.map(doc => (
                  <div key={doc.id} className="px-5 py-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        {tipoBadge(doc.tipo)}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-800 text-sm">{doc.titulo}</span>
                            {!doc.ativo && (
                              <span className="text-xs bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full">Inativo</span>
                            )}
                          </div>
                          <div className="text-xs text-gray-400 mt-0.5">
                            Criado por {doc.criado_por_nome || '—'} · {new Date(doc.criado_em).toLocaleDateString('pt-BR')}
                            {doc.atualizado_em !== doc.criado_em && ` · Editado em ${new Date(doc.atualizado_em).toLocaleDateString('pt-BR')}`}
                          </div>

                          {/* Arquivos anexados */}
                          {doc.arquivos && doc.arquivos.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {doc.arquivos.map(arq => (
                                <div key={arq.id} className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1">
                                  <span className="text-gray-500">{iconeArquivo(arq.tipo)}</span>
                                  <button
                                    onClick={() => baixarArquivo(doc.id, arq.id!)}
                                    className="text-xs text-blue-600 hover:text-blue-800 font-medium max-w-[160px] truncate"
                                    title={arq.nome_original}
                                  >
                                    {arq.nome_original}
                                  </button>
                                  <span className="text-xs text-gray-400">{formatarTamanho(arq.tamanho)}</span>
                                  <button
                                    onClick={() => excluirArquivo(doc.id, arq.id!)}
                                    className="text-gray-300 hover:text-red-500 ml-0.5 leading-none"
                                    title="Remover arquivo"
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Conteúdo expandível */}
                          {expandidos.has(doc.id) && (
                            <pre className="mt-3 text-xs text-gray-600 whitespace-pre-wrap bg-gray-50 rounded-lg p-3 border border-gray-200 max-h-64 overflow-y-auto font-sans leading-relaxed">
                              {doc.conteudo}
                            </pre>
                          )}
                          <button
                            onClick={() => toggleExpandido(doc.id)}
                            className="text-xs text-red-600 hover:text-red-700 mt-1"
                          >
                            {expandidos.has(doc.id) ? '▲ Ocultar conteúdo' : '▼ Ver conteúdo'}
                          </button>
                        </div>
                      </div>

                      {/* Ações */}
                      <div className="flex gap-2 flex-shrink-0">
                        <button
                          onClick={() => abrirEditar(doc)}
                          className="text-xs text-blue-600 hover:text-blue-800 border border-blue-200 hover:border-blue-400 px-3 py-1.5 rounded-lg transition-colors"
                        >
                          Editar
                        </button>
                        {confirmandoId === doc.id ? (
                          <div className="flex gap-1">
                            <button
                              onClick={() => excluir(doc.id)}
                              className="text-xs text-white bg-bravo-primary hover:bg-red-700 px-3 py-1.5 rounded-lg"
                            >
                              Confirmar
                            </button>
                            <button
                              onClick={() => setConfirmandoId(null)}
                              className="text-xs text-gray-500 hover:text-gray-700 border border-gray-300 px-3 py-1.5 rounded-lg"
                            >
                              Cancelar
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setConfirmandoId(doc.id)}
                            className="text-xs text-red-500 hover:text-red-700 border border-red-200 hover:border-red-400 px-3 py-1.5 rounded-lg transition-colors"
                          >
                            Excluir
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal criar/editar */}
      {modalAberto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800">
                {editando ? 'Editar Documento' : 'Novo Documento'}
              </h2>
              <button onClick={() => setModalAberto(false)} className="text-gray-400 hover:text-gray-600 leading-none">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="px-6 py-5 overflow-y-auto flex-1 flex flex-col gap-4">
              {erro && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg">{erro}</div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Departamento *</label>
                  <select
                    value={form.departamento_id}
                    onChange={e => setForm(f => ({ ...f, departamento_id: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="">Selecione...</option>
                    {departamentos.map(d => <option key={d.id} value={d.id}>{d.nome}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1">Tipo *</label>
                  <select
                    value={form.tipo}
                    onChange={e => setForm(f => ({ ...f, tipo: e.target.value as TipoDocumento }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    {TIPOS_DOCUMENTO.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Título *</label>
                <input
                  type="text"
                  value={form.titulo}
                  onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))}
                  placeholder="Ex: POP - Abertura de Cards de Risco"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-semibold text-gray-600">
                    Conteúdo * <span className="text-gray-400 font-normal">(cole o texto ou importe um arquivo)</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={extraindo}
                    className="flex items-center gap-1.5 text-xs font-medium text-red-600 hover:text-red-700 border border-red-300 hover:border-red-400 px-3 py-1 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {extraindo ? (
                      <>
                        <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                        </svg>
                        Salvando...
                      </>
                    ) : (
                      <>
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                        </svg>
                        Importar arquivo
                      </>
                    )}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.docx,.xlsx,.xls,.csv,.txt"
                    onChange={handleImportarArquivo}
                    className="hidden"
                  />
                </div>

                {/* Arquivo importado no modal */}
                {arquivoImportado && (
                  <div className="mb-2 flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                    <span className="text-green-600">{iconeArquivo(arquivoImportado.tipo)}</span>
                    <span className="text-xs text-green-800 font-medium flex-1 truncate">{arquivoImportado.nome_original}</span>
                    <span className="text-xs text-green-600">{formatarTamanho(arquivoImportado.tamanho)}</span>
                    <span className="text-xs text-green-600">· salvo no R2</span>
                    <button
                      onClick={() => setArquivoImportado(null)}
                      className="text-green-400 hover:text-red-500 ml-1"
                      title="Remover arquivo"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}

                <textarea
                  value={form.conteudo}
                  onChange={e => setForm(f => ({ ...f, conteudo: e.target.value }))}
                  placeholder="Cole aqui o conteúdo completo do documento ou use 'Importar arquivo' para extrair de PDF, Word ou Excel..."
                  rows={12}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-none font-mono leading-relaxed"
                />
                <div className="text-xs text-gray-400 mt-1">{form.conteudo.length} caracteres</div>
              </div>

              {editando && (
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={form.ativo}
                    onChange={e => setForm(f => ({ ...f, ativo: e.target.checked }))}
                    className="w-4 h-4 accent-red-600"
                  />
                  <span className="text-sm text-gray-700">Documento ativo (visível para a IA)</span>
                </label>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setModalAberto(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg"
              >
                Cancelar
              </button>
              <button
                onClick={salvar}
                disabled={salvando}
                className="px-5 py-2 text-sm font-semibold text-white bg-bravo-primary hover:bg-red-700 disabled:bg-gray-300 rounded-lg transition-colors"
              >
                {salvando ? 'Salvando...' : editando ? 'Salvar alterações' : 'Criar documento'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentosIAPage;
